/**
 * Sound recordings in the library (a recorder's or mixer's WAV, MP3, M4A, AAC
 * or AIFF), used as a multicam set's separate audio. They are uploaded like
 * library videos (kind 'original-audio'), registered with addAudioRecording,
 * and listed by audioRecordings; the video library leaves them out.
 */

import { gql } from '@apollo/client';
import { client } from '../apolloClient';
import { uploadAssetToR2 } from './r2AssetUpload';
import { currentIdToken } from './freshIdToken';
import { blobByteSource } from '../utils/media/byteSource';
import { AudioTrack, MediaReadError } from '../utils/media/isoAudio';
import { openMediaAudio } from '../utils/media/mediaAudio';
import { readAdtsTrack } from '../utils/media/adtsAudio';
import { Mp3AudioTrack, isMp3Track, readMp3Track } from '../utils/media/mp3Audio';

export interface AudioRecording {
  id: string;
  name: string;
  url: string;
  size: number;
  /** "m:ss", like a video's. */
  duration: string;
  channels: number | null;
  sampleRate: number | null;
  createdAt: string | null;
}

const AUDIO_RECORDING_FIELDS = gql`
  fragment AudioRecordingFields on AudioRecording {
    id
    name
    url
    size
    duration
    channels
    sampleRate
    createdAt
  }
`;

export const AUDIO_RECORDINGS_QUERY = gql`
  query AudioRecordings {
    audioRecordings {
      ...AudioRecordingFields
    }
  }
  ${AUDIO_RECORDING_FIELDS}
`;

export const ADD_AUDIO_RECORDING_MUTATION = gql`
  mutation AddAudioRecording($url: String!, $name: String!, $size: Float!, $duration: String!, $storageKey: String!, $channels: Int, $sampleRate: Int) {
    addAudioRecording(url: $url, name: $name, size: $size, duration: $duration, storageKey: $storageKey, channels: $channels, sampleRate: $sampleRate) {
      ...AudioRecordingFields
    }
  }
  ${AUDIO_RECORDING_FIELDS}
`;

export const fetchAudioRecordings = async (): Promise<AudioRecording[]> => {
  const { data } = await client.query({ query: AUDIO_RECORDINGS_QUERY, fetchPolicy: 'network-only' });
  return Array.isArray(data?.audioRecordings) ? data.audioRecordings : [];
};

/** File types a recording may be, by extension, for when the browser gives none. */
const TYPES_BY_EXTENSION: Record<string, string> = {
  wav: 'audio/wav',
  wave: 'audio/wav',
  bwf: 'audio/wav',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  aifc: 'audio/aiff',
};

export const RECORDING_ACCEPT = 'audio/*,.wav,.wave,.bwf,.mp3,.m4a,.aac,.aif,.aiff,.aifc';

/** The content type to upload a recording as: the browser's, when it is an audio type. */
export const recordingContentType = (file: File): string => {
  if (file.type && file.type.toLowerCase().startsWith('audio/')) return file.type;
  const extension = (/\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1] || '').toLowerCase();
  return TYPES_BY_EXTENSION[extension] || 'audio/wav';
};

/**
 * "1:02:03" lengths are stored as "62:03", like videos'. Whole seconds are
 * rounded down, as videos' are: a stored length past the file's real end made
 * players expect sound where the file had already ended.
 */
export const formatRecordingDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

export interface RecordingFileInfo {
  durationSeconds: number;
  channels: number | null;
  sampleRate: number | null;
}

const isAiffFile = (file: File): boolean =>
  /\.(aif|aiff|aifc)$/i.test(file.name) || /^audio\/(x-)?aiff?(-c)?$/i.test(file.type || '');

/**
 * Whether this browser's players can sound an AIFF. Only Safari can; the
 * others play it silently, so it is refused there before it uploads.
 */
export const browserPlaysAiff = (): boolean => {
  if (typeof document === 'undefined') return true;
  try {
    const probe = document.createElement('audio');
    if (typeof probe.canPlayType !== 'function') return true;
    return Boolean(probe.canPlayType('audio/aiff') || probe.canPlayType('audio/x-aiff'));
  } catch {
    return true;
  }
};

/**
 * Reads a local file's sound header, so a file PithPlay can't use is turned
 * away before it uploads. Throws with a customer-readable message.
 */
export const inspectRecordingFile = async (file: File): Promise<RecordingFileInfo> => {
  if ((file.type || '').toLowerCase().startsWith('video/') || /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(file.name)) {
    throw new Error('That’s a video. Add it as a camera angle, or choose the sound file from your recorder or mixer.');
  }
  const playsAiff = browserPlaysAiff();
  if (isAiffFile(file) && !playsAiff) {
    throw new Error('This browser can’t play AIFF recordings. Export the recording as WAV or MP3 and add that instead.');
  }
  try {
    const source = blobByteSource(file);
    const opened = await openMediaAudio(source);
    if (opened.kind === 'matroska') {
      throw new Error('That’s a video. Add it as a camera angle, or choose the sound file from your recorder or mixer.');
    }
    const codec = opened.track.codec as { sampleRate?: number; channels?: number };
    // Raw AAC, and MP3s that don't say how many frames they hold, are counted
    // frame by frame: quick for a file on this computer, and exact.
    let durationSeconds = opened.track.durationSeconds;
    if (opened.kind === 'adts') {
      durationSeconds = (await readAdtsTrack(source, { buildIndex: true, toS: 0, blockBytes: 4 * 1024 * 1024 })).durationSeconds;
    } else if (opened.kind === 'mp3' && isMp3Track(opened.track as AudioTrack) && (opened.track as Mp3AudioTrack).mp3.frameCount === null) {
      durationSeconds = (await readMp3Track(source, { buildIndex: true, toS: 0, blockBytes: 4 * 1024 * 1024 })).durationSeconds;
    }
    if (!(durationSeconds > 0.5)) throw new MediaReadError('no-audio', 'This recording has no sound in it.');
    return {
      durationSeconds,
      channels: codec.channels && codec.channels > 0 ? codec.channels : null,
      sampleRate: codec.sampleRate && codec.sampleRate > 0 ? Math.round(codec.sampleRate) : null,
    };
  } catch (error) {
    if (error instanceof MediaReadError) {
      if (error.code === 'not-mp4') {
        throw new Error(`PithPlay can’t read this file. Choose a WAV, MP3, M4A${playsAiff ? ', AAC or AIFF' : ' or AAC'} recording.`);
      }
      if (error.code === 'no-audio') throw new Error('This file has no sound in it. Choose the recording from your recorder or mixer.');
      if (/can’t be read here/.test(error.message)) throw new Error(error.message);
      throw new Error('PithPlay can’t read this recording. Export it as a WAV or MP3 and try again.');
    }
    throw error;
  }
};

/**
 * Uploads a recording to the library: the file goes straight to storage,
 * then it is registered as a library recording.
 */
export const uploadAudioRecording = async (
  file: File,
  info: RecordingFileInfo,
  token: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<AudioRecording> => {
  // Keep the file's own lastModified: a resumed upload is matched by it.
  const typed = file.type === recordingContentType(file)
    ? file
    : new File([file], file.name, { type: recordingContentType(file), lastModified: file.lastModified });
  const stored = await uploadAssetToR2(typed, 'original-audio', token, onProgress, signal);
  if (signal?.aborted) {
    const error = new Error('Upload cancelled');
    error.name = 'AbortError';
    throw error;
  }
  const freshToken = await currentIdToken(token);
  const { data } = await client.mutate({
    mutation: ADD_AUDIO_RECORDING_MUTATION,
    variables: {
      url: stored.url,
      name: file.name,
      size: file.size,
      duration: formatRecordingDuration(info.durationSeconds),
      storageKey: stored.objectKey,
      channels: info.channels,
      sampleRate: info.sampleRate,
    },
    context: { headers: { Authorization: `Bearer ${freshToken}` } },
  });
  if (!data?.addAudioRecording?.id) throw new Error('The recording uploaded, but couldn’t be added to your library. Try again.');
  return data.addAudioRecording;
};
